import { Box, useTheme } from '@mui/material';
import dayjs from 'dayjs';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import DashboardActionSection from '@/components/dashboard_components/applicant_dashboard/DashboardActionSection';
import DashboardDataGridSection from '@/components/dashboard_components/applicant_dashboard/DashboardDataGridSection';
import DashboardHeaderSection from '@/components/dashboard_components/applicant_dashboard/DashboardHeaderSection';
import { ApplicantDashboardRow } from '@/components/dashboard_components/applicant_dashboard/ApplicantDashboardTypes';
import useApplicantDashboardData from '@/components/dashboard_components/applicant_dashboard/hooks/useApplicantDashboardData';
import useDashboardActionCardDefinitions from '@/components/dashboard_components/applicant_dashboard/hooks/useDashboardActionCardDefinitions';
import useDashboardColumns from '@/components/dashboard_components/applicant_dashboard/hooks/useDashboardColumns';

import { ApplicationCycleContext } from '@/Contexts/ApplicationCycleContext';
import { ConfigContext } from '@/Contexts/ConfigContext';
import { DashboardContext } from '@/Contexts/DashboardContext';
import { LocaleContext } from '@/Contexts/LocaleContext';
import { SelectedPartnerContext } from '@/Contexts/SelectedPartnerContext';
import { SelectedProgramContext } from '@/Contexts/SelectedProgramContext';
import { SnackbarContext } from '@/Contexts/SnackbarProvider';
import { UsersContext } from '@/Contexts/UsersContext';
import useApplication from '@/hooks/useApplication';
import useApplicationTemplate from '@/hooks/useApplicationTemplate';
import { useDebounce } from '@/hooks/useDebounce';
import { ApplicationDataGridRowProps, NewApplicationType } from '@/models/Application';
import { ApplicationTypes } from '@/models/ApplicationType';
import { programIdToImage, ProgramNames, ProgramProps } from '@/models/Program';
import { ApplicationStatus } from '@/utils/constants';
import { logError } from '@/utils/errorHandlerUtil';
import {
  addApplicationToPartnerApps as addApplicationToPartnerAppsList,
  preparePartnerApps,
} from '@/utils/partnerAppsUtil';
import { getStatusDescription } from '@/utils/statusUtil';
import { navigateToApplicationRoute } from '@/utils/applicationUtil';
import { hasBeenRecognizedForYear, YearsRecognizedByProgram } from '@/utils/YearsRecognizedUtils';
import { Spinner } from '@/components/common/Spinner';

interface DashboardProps {
  currentPartnerId?: number | null;
}

function Dashboard({ currentPartnerId }: DashboardProps) {
  const dashboardData = useApplicantDashboardData(currentPartnerId);

  return dashboardData == null ? (
    <Spinner />
  ) : (
    <DashboardProtected
      partnerApps={dashboardData.partnerApps}
      dashboardRows={dashboardData.dashboardRows}
      selectedProgramInfo={dashboardData.selectedProgramInfo}
    />
  );
}

interface DashboardProtectedProps {
  dashboardRows: ApplicantDashboardRow[];
  partnerApps: ApplicationDataGridRowProps[];
  selectedProgramInfo: ProgramProps;
}

interface ApplicationWindow {
  past: ApplicationDataGridRowProps | null;
  current: ApplicationDataGridRowProps | null;
  next: ApplicationDataGridRowProps | null;
}

function DashboardProtected({
  dashboardRows,
  partnerApps,
  selectedProgramInfo,
}: DashboardProtectedProps) {
  const navigate = useNavigate();
  const theme = useTheme();

  const [downloadingAppId, setDownloadingAppId] = useState<number | null>(null);
  const [downloading, setDownloading] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [hasStateQuestions, setHasStateQuestions] = useState<boolean>(false);

  const {
    postApplication,
    getPrintApplication,
    getSingleAppForApplicantDashboard,
    getAppsForApplicantDashboard,
  } = useApplication();

  const { applicationCycles } = useContext(ApplicationCycleContext);
  const { updatePartnerApps } = useContext(DashboardContext);
  const { setAlert } = useContext(SnackbarContext);
  const { getApplicationTemplate } = useApplicationTemplate();

  const { programLatestAppCycle, programNextAppCycle, programPastAppCycle, portalOpen } =
    useContext(SelectedProgramContext);

  const { currentContact } = useContext(UsersContext);
  const { partnerInfo } = useContext(SelectedPartnerContext);
  const { locale, translatedText, localeLanguageCode } = useContext(LocaleContext);
  const { allStatuses } = useContext(ConfigContext);

  const mainAppLatestAppCycle = useMemo(() => {
    const appTypeId: number = ApplicationTypes.Standard;

    return programLatestAppCycle != null ? programLatestAppCycle[appTypeId] : null;
  }, [programLatestAppCycle]);

  const mainAppNextOpenDate = useMemo(() => {
    const openDate = programNextAppCycle?.[ApplicationTypes.Standard]?.openDate;

    return openDate != null ? dayjs(openDate).format('LL') : null;
  }, [programNextAppCycle]);

  const mainAppPastAppCycle = useMemo(() => {
    return programPastAppCycle != null ? programPastAppCycle[ApplicationTypes.Standard] : null;
  }, [programPastAppCycle]);

  const getApplicationWindow = useCallback(
    (applicationTypeId: ApplicationTypes): ApplicationWindow => {
      const currentCycleId = programLatestAppCycle?.[applicationTypeId]?.id;
      const pastCycleId = programPastAppCycle?.[applicationTypeId]?.id;
      const nextCycleId = programNextAppCycle?.[applicationTypeId]?.id;

      const findByCycleId = (cycleId?: number): ApplicationDataGridRowProps | null => {
        if (cycleId == null) {
          return null;
        }

        return (
          partnerApps.find(
            (app) =>
              app.applicationTypeId === applicationTypeId && app.applicationCycleId === cycleId
          ) ?? null
        );
      };

      return {
        past: findByCycleId(pastCycleId),
        current: findByCycleId(currentCycleId),
        next: findByCycleId(nextCycleId),
      };
    },
    [partnerApps, programLatestAppCycle, programNextAppCycle, programPastAppCycle]
  );

  const mainApplicationWindow = useMemo(
    () => getApplicationWindow(ApplicationTypes.Standard),
    [getApplicationWindow]
  );

  const growthAwardApplicationWindow = useMemo(
    () => getApplicationWindow(ApplicationTypes.GrowthAward),
    [getApplicationWindow]
  );

  const stateQuestionApplicationWindow = useMemo(
    () => getApplicationWindow(ApplicationTypes.StateQuestions),
    [getApplicationWindow]
  );

  const mainApp = useMemo(() => {
    return mainApplicationWindow.current ?? mainApplicationWindow.next;
  }, [mainApplicationWindow]);

  const growthApp = useMemo(() => {
    return growthAwardApplicationWindow.current ?? growthAwardApplicationWindow.next;
  }, [growthAwardApplicationWindow]);

  const stateApp = useMemo(() => {
    return stateQuestionApplicationWindow.current ?? stateQuestionApplicationWindow.next;
  }, [stateQuestionApplicationWindow]);

  const getButtonText = (status: ApplicationStatus | null): string => {
    if (status == null || allStatuses == null) {
      return '';
    }
    const statusDescription = getStatusDescription(allStatuses, status, localeLanguageCode);
    return statusDescription != null ? statusDescription : '';
  };
  const handleDownloadApplication = useCallback(
    async (applicationId: number) => {
      setDownloadingAppId(applicationId);
      setDownloading(true);

      try {
        await getPrintApplication(applicationId);

        setDownloadingAppId(null);
        setDownloading(false);
      } catch {
        setDownloadingAppId(null);
        setDownloading(false);
        setAlert({
          severity: 'error',
          message: translatedText.common.genericError,
        });
      }
    },
    [getPrintApplication, setAlert, translatedText.common.genericError]
  );

  const columns = useDashboardColumns({
    translatedText,
    selectedProgramId: selectedProgramInfo.id,
    downloading,
    downloadingAppId,
    taigaColor: theme.palette.taiga.main,
    handleDownloadApplication,
  });

  useEffect(() => {
    const fetchData = async (applicationTemplateId: number) => {
      const result = await getApplicationTemplate(applicationTemplateId);
      if (result != null) {
        setHasStateQuestions(true);
      } else {
        setHasStateQuestions(false);
      }
    };

    if (
      applicationCycles != null &&
      applicationCycles.length > 0 &&
      partnerInfo != null &&
      partnerInfo.programId == ProgramNames.TreeCityUSA &&
      partnerInfo.stateId != null
    ) {
      const activeStateQuestionApplicationCycle = applicationCycles.find(
        (ac) =>
          ac.active &&
          ac.programId == partnerInfo.programId &&
          ac.stateId == partnerInfo.stateId &&
          ac.applicationTypeId == ApplicationTypes.StateQuestions
      );

      if (activeStateQuestionApplicationCycle?.applicationTemplateId != null) {
        void fetchData(activeStateQuestionApplicationCycle.applicationTemplateId);
      } else {
        setHasStateQuestions(false);
      }
    }
  }, [partnerInfo, getApplicationTemplate, applicationCycles]);

  useEffect(() => {
    if (partnerInfo != null && selectedProgramInfo != null && translatedText != null) {
      setLoading(false);
    }
  }, [partnerInfo, selectedProgramInfo, translatedText]);

  const shouldShowGrowthAwardForProgram = (programId: ProgramNames): boolean => {
    switch (programId) {
      case ProgramNames.TreeCityUSA:
        return (
          YearsRecognizedByProgram(
            partnerInfo?.partnerYearsRecognized ?? [],
            ProgramNames.TreeCityUSA
          ) > 0
        );
      case ProgramNames.TreeCitiesWorld:
        return (
          partnerInfo?.partnerYearsRecognized != null &&
          partnerInfo?.partnerYearsRecognized.length > 0 &&
          mainAppPastAppCycle != null &&
          hasBeenRecognizedForYear(
            partnerInfo.partnerYearsRecognized,
            ProgramNames.TreeCitiesWorld,
            ApplicationTypes.Standard,
            mainAppPastAppCycle.id
          )
        );
      case ProgramNames.TreeLineUSA:
      case ProgramNames.TreeCampusHigherEducation:
      case ProgramNames.TreeCampusHealthcare:
      case ProgramNames.TreeCampusK12:
      case ProgramNames._:
      default:
        return false;
    }
  };

  const showGrowthAwardBox: boolean =
    shouldShowGrowthAwardForProgram(selectedProgramInfo.id) &&
    programLatestAppCycle != null &&
    programLatestAppCycle[ApplicationTypes.GrowthAward] != null;

  const disabledGrowthAwardButton: boolean =
    mainApp == null || (mainApp?.status === ApplicationStatus.InProgress && growthApp == null);
  const showStateQuestionsBox =
    hasStateQuestions && selectedProgramInfo.id == ProgramNames.TreeCityUSA;

  const isMainApplicationApproved = mainApp?.status === ApplicationStatus.ApplicationApproved;
  const hasToolkitLink =
    selectedProgramInfo.toolkitLink != null && selectedProgramInfo.toolkitLink.trim().length > 0;
  const isToolkitEnabled = isMainApplicationApproved && hasToolkitLink;

  const openProgramToolkit = () => {
    if (!isToolkitEnabled || selectedProgramInfo.toolkitLink == null) {
      return;
    }

    window.open(selectedProgramInfo.toolkitLink, '_blank', 'noopener,noreferrer');
  };

  const [mainActionLocked, setMainActionLocked] = useState(false);
  const [growthActionLocked, setGrowthActionLocked] = useState(false);
  const [stateActionLocked, setStateActionLocked] = useState(false);

  const debouncedMainActionLocked = useDebounce(mainActionLocked, 3000);
  const debouncedGrowthActionLocked = useDebounce(growthActionLocked, 3000);
  const debouncedStateActionLocked = useDebounce(stateActionLocked, 3000);

  useEffect(() => {
    if (mainActionLocked && debouncedMainActionLocked) {
      setMainActionLocked(false);
    }
  }, [mainActionLocked, debouncedMainActionLocked]);

  useEffect(() => {
    if (growthActionLocked && debouncedGrowthActionLocked) {
      setGrowthActionLocked(false);
    }
  }, [growthActionLocked, debouncedGrowthActionLocked]);

  useEffect(() => {
    if (stateActionLocked && debouncedStateActionLocked) {
      setStateActionLocked(false);
    }
  }, [stateActionLocked, debouncedStateActionLocked]);

  const handleMainAction = async () => {
    if (mainActionLocked) {
      return;
    }

    setMainActionLocked(true);

    if (mainApp?.applicationId != null) {
      continueApp(selectedProgramInfo.id, ApplicationTypes.Standard);
      return;
    }

    await createApp(ApplicationTypes.Standard);
  };

  const handleGrowthAwardAction = async () => {
    if (growthActionLocked) {
      return;
    }

    setGrowthActionLocked(true);

    if (growthApp?.applicationId != null) {
      continueApp(selectedProgramInfo.id, ApplicationTypes.GrowthAward);
      return;
    }

    await createApp(ApplicationTypes.GrowthAward);
  };

  const handleStateQuestionsAction = async () => {
    if (stateActionLocked) {
      return;
    }

    setStateActionLocked(true);

    if (stateApp?.applicationId != null) {
      continueApp(selectedProgramInfo.id, ApplicationTypes.StateQuestions);
      return;
    }

    await createApp(ApplicationTypes.StateQuestions);
  };

  const { mainActionCard, growthAwardActionCard, stateQuestionsActionCard } =
    useDashboardActionCardDefinitions({
      translatedText,
      selectedProgramName: selectedProgramInfo.programName,
      mainAppLatestCycleName: mainAppLatestAppCycle?.applicationCycleName,
      mainAppNextOpenDate,
      loading,
      portalOpenMain: portalOpen[ApplicationTypes.Standard],
      portalOpenGrowth: portalOpen[ApplicationTypes.GrowthAward],
      portalOpenState: portalOpen[ApplicationTypes.StateQuestions],
      disabledGrowthAwardButton,
      hasMainApplication: mainApp?.applicationId != null,
      hasGrowthApplication: growthApp?.applicationId != null,
      hasStateApplication: stateApp?.applicationId != null,
      mainStatusButtonText: getButtonText(mainApp?.status ?? null),
      growthStatusButtonText: getButtonText(growthApp?.status ?? null),
      stateStatusButtonText: getButtonText(stateApp?.status ?? null),
      mainApplicationStatus: mainApp?.status,
      stateApplicationStatus: stateApp?.status,
      onMainActionClick: () => {
        void handleMainAction();
      },
      onGrowthActionClick: () => {
        void handleGrowthAwardAction();
      },
      onStateQuestionsActionClick: () => {
        void handleStateQuestionsAction();
      },
    });

  return loading ? (
    <Spinner />
  ) : (
    <Box
      sx={{
        width: '100%',
        maxWidth: '100%',
        height: '100%',
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        overflowX: 'clip',
        overflowY: 'hidden',
      }}
    >
      <DashboardHeaderSection
        partnerName={partnerInfo?.partnerName}
        programIconSrc={programIdToImage[selectedProgramInfo.id]}
      />
      <Box
        sx={{
          width: '100%',
          maxWidth: '100%',
          flex: '1 1 auto',
          minHeight: 0,
          overflowX: 'clip',
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <DashboardActionSection
          mainActionCard={mainActionCard}
          growthAwardActionCard={growthAwardActionCard}
          stateQuestionsActionCard={stateQuestionsActionCard}
          showGrowthAward={showGrowthAwardBox}
          showStateQuestions={showStateQuestionsBox}
          toolkitTitle={`${selectedProgramInfo.programName} Toolkit`}
          toolkitDescription='Resources to support your program participation.'
          toolkitButtonLabel={`${translatedText.dashboard.accessToolkit}`}
          toolkitPendingApprovalLabel={`${translatedText.dashboard.accessToolkitPendingApproval}`}
          toolkitEnabled={isToolkitEnabled}
          onOpenToolkit={openProgramToolkit}
        />

        <DashboardDataGridSection
          locale={locale}
          rows={dashboardRows}
          columns={columns}
          title={translatedText.dashboard.labelPastApplications.toUpperCase()}
          emptyMessage={translatedText.dashboard.messageNoApplications}
        />
      </Box>
    </Box>
  );

  function continueApp(programId: ProgramNames, appType: ApplicationTypes) {
    const applicationIdByType: Partial<Record<ApplicationTypes, number | null | undefined>> = {
      [ApplicationTypes.Standard]: mainApp?.applicationId,
      [ApplicationTypes.StateQuestions]: stateApp?.applicationId,
      [ApplicationTypes.GrowthAward]: growthApp?.applicationId,
    };

    navigateToApplicationRoute(navigate, programId, appType, applicationIdByType[appType], {
      hasStateQuestions,
    });
  }

  async function createApp(appType: ApplicationTypes) {
    setAlert({
      severity: 'info',
      message: translatedText?.dashboard?.messageCreateApplication,
    });

    if (partnerInfo?.id == null || currentContact?.id == null || programLatestAppCycle == null) {
      return;
    }

    const appCycleId = programLatestAppCycle[appType]?.id;

    if (appCycleId == null) {
      setAlert({
        severity: 'error',
        message: translatedText.dashboard.messageNoCycleFound,
      });

      return;
    }

    const postData: NewApplicationType = {
      partnerId: partnerInfo.id,
      contactId: currentContact.id,
      programId: selectedProgramInfo.id,
      applicationTypeId: Number(appType),
      applicationCycleId: appCycleId,
    };

    setLoading(true);

    let result;
    try {
      result = await postApplication(postData);
    } catch {
      setAlert({
        severity: 'error',
        message: translatedText.dashboard.messageCreateApplicationError,
      });
      // The dashboard may be out of date (e.g. the application was created in another browser),
      // so reload it to show the application that is already there instead of "Create".
      await refreshPartnerApps(partnerInfo.id);
      setLoading(false);
      return;
    }

    if (result?.applicationId == null) {
      setAlert({
        severity: 'error',
        message: translatedText.dashboard.messageCreateApplicationError,
      });
      setLoading(false);
      return;
    }

    // Update the dashboard before navigating away so it is current when the user comes back.
    await addApplicationToPartnerApps(result.applicationId);

    if (result?.status != null && result.status.toLowerCase() == 'success') {
      navigateToApplicationRoute(navigate, selectedProgramInfo.id, appType, result.applicationId, {
        hasStateQuestions,
      });
    }

    setLoading(false);
  }

  async function addApplicationToPartnerApps(applicationId: number) {
    try {
      const newApp = await getSingleAppForApplicantDashboard(applicationId);
      if (newApp == null) {
        return;
      }

      updatePartnerApps(addApplicationToPartnerAppsList(partnerApps, newApp));
    } catch (error) {
      // Not worth blocking the navigation to the application; the dashboard reloads when revisited.
      logError(`Error adding application ${applicationId} to the dashboard.`, error);
    }
  }

  async function refreshPartnerApps(partnerId: number) {
    try {
      const apps = await getAppsForApplicantDashboard(partnerId);
      updatePartnerApps(preparePartnerApps(apps));
    } catch (error) {
      logError(`Error reloading the applications for partner ${partnerId}.`, error);
    }
  }
}

export default Dashboard;
